
import React, { useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Upload, Link, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { Creative } from "../../../types";
import CreativePreview from "./CreativePreview";

interface AlanCreativeUploadProps {
  creatives: Creative[];
  onMediaOwnerFileChange: (mediaOwnerName: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveMediaOwnerCreative: (mediaOwnerName: string, index: number) => void;
  onUpdateMediaOwnerCreativeDays?: (mediaOwnerName: string, creativeIndex: number, days: number[]) => void;
  isUploading: boolean;
  onUrlSubmit?: (mediaOwnerName: string, url: string, orientation: 'landscape' | 'portrait') => void;
}

const AlanCreativeUpload = ({
  creatives,
  onMediaOwnerFileChange,
  onRemoveMediaOwnerCreative,
  onUpdateMediaOwnerCreativeDays,
  isUploading,
  onUrlSubmit
}: AlanCreativeUploadProps) => {
  const { t, language } = useLanguage();
  const portraitFileInputRef = useRef<HTMLInputElement>(null);
  const landscapeFileInputRef = useRef<HTMLInputElement>(null);
  
  const [showLandscapeUrlInput, setShowLandscapeUrlInput] = useState(false);
  const [showPortraitUrlInput, setShowPortraitUrlInput] = useState(false);
  const [landscapeUrl, setLandscapeUrl] = useState("");
  const [portraitUrl, setPortraitUrl] = useState("");
  const [landscapeUrlError, setLandscapeUrlError] = useState("");
  const [portraitUrlError, setPortraitUrlError] = useState("");

  const handlePortraitAreaClick = () => {
    portraitFileInputRef.current?.click();
  };

  const handleLandscapeAreaClick = () => {
    landscapeFileInputRef.current?.click();
  };

  const landscapeCreative = creatives.find(creative => creative.orientation === 'landscape');
  const portraitCreative = creatives.find(creative => creative.orientation === 'portrait');

  // Calculate taken days for each creative (days used by OTHER creatives)
  const getTakenDaysForCreative = (creativeIndex: number): number[] => {
    const taken: number[] = [];
    creatives.forEach((c, idx) => {
      if (idx !== creativeIndex && c.scheduledDays) {
        taken.push(...c.scheduledDays);
      }
    });
    return [...new Set(taken)];
  };

  const handleLandscapeFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      (e.target as any).orientation = 'landscape';
      (e.target as any).isAlan = true;
      onMediaOwnerFileChange("", e); // Pass empty - wrapper will use location name
    }
  };

  const handlePortraitFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      (e.target as any).orientation = 'portrait';
      (e.target as any).isAlan = true;
      onMediaOwnerFileChange("", e); // Pass empty - wrapper will use location name
    }
  };

  const validateUrl = (url: string, setError: (error: string) => void): boolean => {
    try {
      const parsed = new URL(url);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        setError(language === 'ar' ? 'يجب أن يبدأ الرابط بـ http:// أو https://' : 'URL must start with http:// or https://');
        return false;
      }
      setError("");
      return true;
    } catch {
      setError(language === 'ar' ? 'الرابط غير صالح' : 'Invalid URL format');
      return false;
    }
  };

  const handleLandscapeUrlSubmit = () => {
    const trimmedUrl = landscapeUrl.trim();
    if (!trimmedUrl) {
      setLandscapeUrlError(language === 'ar' ? 'الرجاء إدخال رابط' : 'Please enter a URL');
      return;
    }
    
    if (!validateUrl(trimmedUrl, setLandscapeUrlError)) {
      return;
    }

    if (onUrlSubmit) {
      onUrlSubmit("", trimmedUrl, 'landscape'); // Pass empty - wrapper will use location name
      setLandscapeUrl("");
      setShowLandscapeUrlInput(false);
    }
  };

  const handlePortraitUrlSubmit = () => {
    const trimmedUrl = portraitUrl.trim();
    if (!trimmedUrl) {
      setPortraitUrlError(language === 'ar' ? 'الرجاء إدخال رابط' : 'Please enter a URL');
      return;
    }
    
    if (!validateUrl(trimmedUrl, setPortraitUrlError)) {
      return;
    }

    if (onUrlSubmit) {
      onUrlSubmit("", trimmedUrl, 'portrait'); // Pass empty - wrapper will use location name
      setPortraitUrl("");
      setShowPortraitUrlInput(false);
    }
  };

  const renderUploadArea = (
    orientation: 'landscape' | 'portrait',
    creative: Creative | undefined,
    showUrlInput: boolean,
    setShowUrlInput: (show: boolean) => void,
    urlValue: string,
    setUrlValue: (value: string) => void,
    urlError: string,
    setUrlError: (error: string) => void,
    handleUrlSubmit: () => void,
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    handleAreaClick: () => void,
    fileInputRef: React.RefObject<HTMLInputElement>
  ) => {
    const dimensions = orientation === 'landscape' ? '1920 × 1080 px' : '1080 × 1920 px';
    const aspectRatio = orientation === 'landscape' ? 'video' : 'portrait';

    if (creative) {
      const index = creatives.findIndex((c) => c.orientation === orientation);
      return (
        <CreativePreview
          creative={creative}
          creativeIndex={index}
          onRemove={() => {
            if (index !== -1) {
              onRemoveMediaOwnerCreative("", index); // Pass empty - wrapper will use location name
            }
          }}
          onUpdateScheduledDays={
            onUpdateMediaOwnerCreativeDays && index !== -1
              ? (days) => onUpdateMediaOwnerCreativeDays("", index, days)
              : undefined
          }
          takenDays={index !== -1 ? getTakenDaysForCreative(index) : []}
          showScheduling={creatives.length > 1}
          isUploading={isUploading}
          aspectRatio={aspectRatio as 'video' | 'portrait'}
        />
      );
    }

    if (showUrlInput) {
      return (
        <div className="border-2 border-dashed rounded-lg p-4 border-primary/30 bg-primary/5">
          <div className="flex flex-col items-center space-y-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowUrlInput(false);
                setUrlValue("");
                setUrlError("");
              }}
              className="self-start -mt-1 -ml-1"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {language === 'ar' ? 'رجوع' : 'Back'}
            </Button>
            
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Link className="h-5 w-5 text-primary" />
            </div>
            
            <div className="w-full space-y-2">
              <p className="text-center text-xs font-medium text-foreground">
                {language === 'ar' ? 'أدخل رابط الملفات' : 'Enter a link to your files'}
              </p>
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={urlValue}
                  onChange={(e) => {
                    setUrlValue(e.target.value);
                    setUrlError("");
                  }}
                  placeholder="https://drive.google.com/..."
                  className="flex-1 text-xs h-8"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleUrlSubmit();
                    }
                  }}
                />
                <Button size="sm" className="h-8" onClick={handleUrlSubmit} disabled={!urlValue.trim()}>
                  {language === 'ar' ? 'إضافة' : 'Add'}
                </Button>
              </div>
              {urlError && (
                <p className="text-xs text-destructive text-center">{urlError}</p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors hover:bg-accent/5"
        onClick={handleAreaClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/jpeg,image/png,image/gif,video/mp4,video/mpeg,video/quicktime,video/x-msvideo"
          onChange={handleFileChange}
        />
        <div className="flex flex-col items-center gap-2">
          <Upload className="h-6 w-6 text-muted-foreground" />
          <p className="text-xs text-muted-foreground mt-1">
            {t('upload')}
          </p>
          
          {onUrlSubmit && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowUrlInput(true);
              }}
              className="mt-2 text-xs h-7"
            >
              <Link className="h-3 w-3 mr-1" />
              {language === 'ar' ? 'أو رابط' : 'Or link'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      {/* Landscape Upload Area */}
      <div className="border rounded-lg p-3">
        <div className="mb-2 flex items-center">
          <span className="text-sm font-medium">Landscape</span>
          <span className="text-xs text-muted-foreground ml-2">(1920 × 1080 px)</span>
        </div>
        
        {renderUploadArea(
          'landscape',
          landscapeCreative,
          showLandscapeUrlInput,
          setShowLandscapeUrlInput,
          landscapeUrl,
          setLandscapeUrl,
          landscapeUrlError,
          setLandscapeUrlError,
          handleLandscapeUrlSubmit,
          handleLandscapeFileChange,
          handleLandscapeAreaClick,
          landscapeFileInputRef
        )}
      </div>

      {/* Portrait Upload Area */}
      <div className="border rounded-lg p-3">
        <div className="mb-2 flex items-center">
          <span className="text-sm font-medium">Portrait</span>
          <span className="text-xs text-muted-foreground ml-2">(1080 × 1920 px)</span>
        </div>
        
        {renderUploadArea(
          'portrait',
          portraitCreative,
          showPortraitUrlInput,
          setShowPortraitUrlInput,
          portraitUrl,
          setPortraitUrl,
          portraitUrlError,
          setPortraitUrlError,
          handlePortraitUrlSubmit,
          handlePortraitFileChange,
          handlePortraitAreaClick,
          portraitFileInputRef
        )}
      </div>
    </div>
  );
};

export default AlanCreativeUpload;
